import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import QuestionCard from "@/components/QuestionCard";
import { KineticText, Reveal } from "@/components/motion/primitives";
import { SectionNav, BackToTop } from "@/components/ChapterNav";
import { FACET_META } from "@/lib/explore";
import { ChevronLeft, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { BackToSearch } from "@/components/search/BackToSearch";
import { LoadError, isNotFound } from "@/components/LoadError";

const bookId = (b) => `book-${b.toLowerCase().replace(/\s+/g, "-")}`;

export default function FacetPage() {
  const { facet, name } = useParams();
  const [sp, setSp] = useSearchParams();
  const role = facet === "people" ? sp.get("role") || "all" : null;
  const meta = FACET_META[facet];
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["browse", facet, name, role],
    queryFn: async () => (await api.get(`/browse/${facet}/${encodeURIComponent(name)}`, { params: role && role !== "all" ? { role } : {} })).data,
    placeholderData: (prev) => prev,
  });
  const setRole = (r) => setSp(r === "all" ? {} : { role: r }, { replace: true });

  if (isLoading) {
    return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  if (isError && !isNotFound(error)) return <LoadError onRetry={refetch} />;
  if (isError || !data || !meta) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-serif text-3xl font-bold text-foreground">Not found</h1>
        <p className="mt-3 text-muted-foreground">We don&apos;t have questions catalogued for &ldquo;{name}&rdquo; yet.</p>
        <Link to={`/explore/${facet}`} className="mt-6 inline-block text-primary hover:underline">Back to the list</Link>
      </div>
    );
  }

  const items = data.groups.map((g) => ({ key: g.book, label: g.book, count: g.questions.length }));

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8" data-testid="facet-page">
      <Seo title={`${data.name}: ${data.count} questions ${meta.noun}`} description={`${data.count} questions ${meta.noun} across ${data.groups.length} books of the Bible.`} />
      <BackToSearch className="mb-6 mr-5" />
      <Link to={`/explore/${facet}`} data-testid="facet-back-link" className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> {meta.title}
      </Link>

      <header className="mb-10 border-b border-border pb-8">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">{data.label}</span>
        <KineticText as="h1" text={data.name} className="mt-3 block font-serif text-5xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-6xl" testId="facet-title" />
        <p className="mt-3 font-mono text-sm text-muted-foreground" data-testid="facet-count">
          {data.count} question{data.count === 1 ? "" : "s"} {role === "asked" ? `${data.name} asked` : role === "received" ? `${data.name} was asked` : meta.noun} across {data.groups.length} book{data.groups.length === 1 ? "" : "s"}
        </p>
        {data.counts && (
          <div className="mt-5 inline-flex overflow-hidden rounded-full border border-border bg-card font-mono text-[11px] uppercase tracking-wider" data-testid="role-switch">
            {[["all", `All · ${data.counts.total}`], ["asked", `Asked by ${data.name} · ${data.counts.asked}`], ["received", `Asked to ${data.name} · ${data.counts.received}`]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setRole(k)} data-testid={`role-${k}`} disabled={k !== "all" && !data.counts[k]}
                className={`px-3 py-1.5 transition-colors disabled:opacity-40 ${role === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
        )}
      </header>

      <SectionNav items={items} idFor={bookId} label="Book" testId="book-nav" />

      <div className="space-y-14">
        {data.groups.map((g) => (
          <section key={g.book} id={bookId(g.book)} data-section={g.book} className="scroll-mt-32" data-testid={`facet-book-${bookId(g.book)}`}>
            <div className="mb-6 flex items-center gap-3">
              <Link to={`/book/${encodeURIComponent(g.book)}`} className="font-serif text-3xl font-semibold text-foreground hover:text-primary">{g.book}</Link>
              <span className="h-px flex-1 bg-border" />
              <span className="font-mono text-xs text-muted-foreground">{g.questions.length}</span>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {g.questions.map((q, i) => (
                <Reveal key={q.id} delay={(i % 2) * 0.08}>
                  <QuestionCard q={q} index={i} />
                </Reveal>
              ))}
            </div>
          </section>
        ))}
      </div>
      <BackToTop />
    </div>
  );
}
